import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "dashboard_values_hidden";
export const DASHBOARD_VALUES_PRIVACY_EVENT = "dashboardValuesPrivacyChanged";
const AUTO_HIDE_MS = 15000;

let autoHideTimerId = null;

function readHidden() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Default: amounts blurred. Only show when user explicitly chose "0".
    if (stored == null) return true;
    return stored !== "0";
  } catch {
    return true;
  }
}

function clearAutoHideTimer() {
  if (autoHideTimerId != null) {
    clearTimeout(autoHideTimerId);
    autoHideTimerId = null;
  }
}

function writeHidden(next) {
  try {
    localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent(DASHBOARD_VALUES_PRIVACY_EVENT, {
      detail: { valuesHidden: next },
    }),
  );
}

function scheduleAutoHide() {
  clearAutoHideTimer();
  autoHideTimerId = setTimeout(() => {
    autoHideTimerId = null;
    writeHidden(true);
  }, AUTO_HIDE_MS);
}

function setValuesHiddenState(next) {
  writeHidden(next);
  if (next) {
    clearAutoHideTimer();
  } else {
    // Revealed — auto-blur again after 15s
    scheduleAutoHide();
  }
}

/** Shared toggle for masking sensitive dashboard amounts (footer eye button). */
export function useDashboardValuePrivacy() {
  const [valuesHidden, setValuesHidden] = useState(readHidden);

  useEffect(() => {
    const sync = () => setValuesHidden(readHidden());
    window.addEventListener(DASHBOARD_VALUES_PRIVACY_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(DASHBOARD_VALUES_PRIVACY_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // If amounts are already visible on mount (e.g. refresh), restart auto-hide.
  useEffect(() => {
    if (!readHidden()) {
      scheduleAutoHide();
    }
  }, []);

  const toggleValuesHidden = useCallback(() => {
    setValuesHiddenState(!readHidden());
  }, []);

  return { valuesHidden, toggleValuesHidden };
}
